import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { DashboardLayout } from "@/components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Key, CheckCircle, Loader2, Clock } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ClaimSociety = () => {
  const navigate = useNavigate();
  const [claimCode, setClaimCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [pendingApproval, setPendingApproval] = useState(false);

  const handleClaim = async (e) => {
    e.preventDefault();
    if (!claimCode.trim()) {
      toast.error("Please enter a claim code");
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API}/societies/claim?claim_code=${claimCode.trim().toUpperCase()}`, {
        method: "POST",
        credentials: "include"
      });

      const result = await res.json();
      
      if (res.ok) {
        if (result.status === "pending_approval") {
          setPendingApproval(true);
          toast.success("Claim request submitted! Awaiting admin approval.");
        } else {
          toast.success("Society claimed successfully!");
          navigate("/dashboard");
        }
      } else {
        toast.error(result.detail || "Invalid claim code");
      }
    } catch (error) {
      toast.error("Failed to claim society");
    } finally {
      setLoading(false);
    }
  };

  if (pendingApproval) {
    return (
      <DashboardLayout>
        <div className="max-w-md mx-auto py-16">
          <Card>
            <CardContent className="py-12 text-center">
              <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock className="w-8 h-8 text-amber-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope' }}>
                Claim Request Submitted
              </h3>
              <p className="text-slate-600 mb-4">
                Your claim request is pending admin approval. You'll receive an email once it's approved.
              </p>
              <p className="text-sm text-slate-500">
                The admin will review your request and either approve or reject it.
              </p>
              <Button
                onClick={() => navigate("/dashboard")}
                variant="outline"
                className="mt-6"
              >
                Return to Dashboard
              </Button>
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-md mx-auto py-16">
        <Card>
          <CardHeader className="text-center">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Key className="w-8 h-8 text-emerald-700" />
            </div>
            <CardTitle style={{ fontFamily: 'Manrope' }}>Claim Your Society</CardTitle>
            <CardDescription>
              Enter the claim code provided by your admin to request ownership of your society profile
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleClaim} className="space-y-4">
              <div>
                <Label htmlFor="claimCode">Claim Code</Label>
                <Input
                  id="claimCode"
                  value={claimCode}
                  onChange={(e) => setClaimCode(e.target.value.toUpperCase())}
                  placeholder="e.g., ABC12XYZ"
                  className="text-center text-lg font-mono tracking-wider"
                  maxLength={8}
                  data-testid="claim-code-input"
                />
              </div>
              <Button 
                type="submit" 
                className="w-full bg-emerald-700 hover:bg-emerald-800"
                disabled={loading}
                data-testid="claim-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Submit Claim Request
                  </>
                )}
              </Button>
            </form>
            
            <div className="mt-6 p-4 bg-amber-50 rounded-lg border border-amber-200">
              <p className="text-sm text-amber-800">
                <strong>Note:</strong> Claim requests require admin approval. 
                Once submitted, the admin will review and approve your request.
              </p>
            </div>
            
            <div className="mt-4 p-4 bg-slate-50 rounded-lg">
              <p className="text-sm text-slate-600">
                <strong>Don't have a claim code?</strong><br />
                Contact your society admin or the platform administrator to get your unique claim code.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};
